import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { Icon } from '../components/Icon.js';
import { fitWaveform, formatVoiceTime, MAX_VOICE_NOTE_MS } from './voiceNote.js';
import styles from './VoicePlayer.module.css';

const BARS = 40;
const STEP_S = 5;

let playing: HTMLAudioElement | null = null;

interface VoicePlayerProps {
  /** Absent while a bucket link is still resolving. */
  src?: string;
  /** From the sender: a recorded WebM reports `Infinity` until fully played. */
  durationS: number;
  waveform: number[];
  onPlay?: () => void;
  onError?: () => void;
}

export function VoicePlayer({ src, durationS, waveform, onPlay, onError }: VoicePlayerProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [positionS, setPositionS] = useState(0);
  const [mediaS, setMediaS] = useState(0);
  const bars = fitWaveform(waveform, BARS);
  const totalS = durationS > 0 ? durationS : mediaS;
  const progress = totalS > 0 ? Math.min(positionS / totalS, 1) : 0;

  useEffect(() => {
    const el = audio.current;
    return () => {
      if (playing === el) playing = null;
    };
  }, []);

  const toggle = () => {
    const el = audio.current;
    if (!el || !src) return;
    if (!el.paused) return el.pause();
    if (playing && playing !== el) playing.pause();
    playing = el;
    el.play().catch(() => setPlaying(false));
  };

  const seek = (toS: number) => {
    const el = audio.current;
    if (!el || !src || totalS <= 0) return;
    el.currentTime = Math.min(Math.max(toS, 0), totalS);
    setPositionS(el.currentTime);
  };

  const onBarsClick = (event: MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    seek(((event.clientX - box.left) / box.width) * totalS);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: -STEP_S, ArrowRight: STEP_S }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    seek(positionS + step);
  };

  const started = isPlaying || positionS > 0;
  return (
    <div className={styles.player}>
      <audio
        ref={audio}
        src={src}
        preload="none"
        onPlay={() => {
          setPlaying(true);
          onPlay?.();
        }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setPositionS(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const { duration } = event.currentTarget;
          if (Number.isFinite(duration)) setMediaS(duration);
        }}
        onEnded={(event) => {
          event.currentTarget.currentTime = 0;
          setPositionS(0);
        }}
        onError={onError}
      />
      <button
        type="button"
        className={styles.play}
        onClick={toggle}
        disabled={!src}
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
      >
        <Icon name={isPlaying ? 'pause' : 'play'} size={14} />
      </button>
      <div
        className={styles.bars}
        role="slider"
        tabIndex={0}
        aria-label="Position"
        aria-valuemin={0}
        aria-valuemax={Math.round(totalS)}
        aria-valuenow={Math.round(positionS)}
        aria-valuetext={`${formatVoiceTime(positionS * 1000)} of ${formatVoiceTime(totalS * 1000)}`}
        onClick={onBarsClick}
        onKeyDown={onKeyDown}
      >
        {bars.map((level, i) => (
          <span
            key={i}
            className={(i + 0.5) / bars.length <= progress ? styles.barPlayed : styles.bar}
            style={{ height: `${Math.max(level, 0.1) * 100}%` }}
          />
        ))}
      </div>
      <span className={styles.clock}>{formatVoiceTime((started ? positionS : totalS) * 1000)}</span>
    </div>
  );
}

export function RecordingIndicator({ elapsedMs }: { elapsedMs: number }) {
  return (
    <div className={styles.recording} role="status" aria-label="Recording">
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.clock}>
        {formatVoiceTime(elapsedMs)}
        <span className={styles.limit}> / {formatVoiceTime(MAX_VOICE_NOTE_MS)}</span>
      </span>
    </div>
  );
}
